'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Send,
  FileText,
  ShieldCheck,
  QrCode,
  CheckCircle,
  Sparkles,
  PhoneOff,
  MessageSquare,
  FileCheck,
  User,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';

interface ChatMessage {
  id: string;
  sender: 'doctor' | 'patient';
  text: string;
  time: string;
}

export function TelemedicineSimulatorSection() {
  // Video Controls State
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [activeTab, setActiveTab] = useState<'video' | 'prescription' | 'certificate'>('video');

  // Interactive Chat State
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'doctor',
      text: 'Olá! Sou a Dra. Camila Ramos. Estou analisando seu histórico. Como posso te ajudar hoje?',
      time: '14:02',
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Quick Prompt Helpers
  const quickPrompts = [
    'Estou com dor de cabeça e febre leve',
    'Preciso de renovação de receita médica',
    'Necessito de atestado para repouso',
  ];

  const handleSendMessage = (textToSend?: string) => {
    const text = (textToSend || inputMessage).trim();
    if (!text) return;

    const newMsg: ChatMessage = {
      id: Date.now().toString(),
      sender: 'patient',
      text,
      time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, newMsg]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate doctor intelligent response
    setTimeout(() => {
      let doctorReply =
        'Compreendo perfeitamente os seus sintomas. Acabei de gerar sua prescrição digital assinada via ICP-Brasil com QR Code e seu atestado médico. Você já pode visualizá-los na aba ao lado!';
      if (text.toLowerCase().includes('receita')) {
        doctorReply =
          'Perfeito! Sua receita médica com certificação ICP-Brasil e dispensação digital foi emitida. Já está pronta para visualização e compra na farmácia!';
      } else if (text.toLowerCase().includes('atestado')) {
        doctorReply =
          'Sem problemas! Conforme avaliação clínica, emiti seu atestado médico com dias de repouso e validação digital oficial do CFM.';
      }

      const replyMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'doctor',
        text: doctorReply,
        time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, replyMsg]);
      setIsTyping(false);
    }, 1200);
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-background via-blue-950/15 to-background border-b border-border/80 overflow-hidden relative">
      {/* Ambient background glows */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 rounded-full bg-blue-500/10 blur-[130px] pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 rounded-full bg-cyan-500/10 blur-[130px] pointer-events-none" />

      <div className="container-landing relative z-10">
        {/* Section Header - Alinhado à esquerda */}
        <div className="text-left max-w-3xl mb-12 space-y-3">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/25 text-primary text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-blue-500" />
            <span>Simulador Interativo da Consulta</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-foreground tracking-tight">
            Experimente a Telemedicina Medikei
          </h2>

          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            Teste como funciona nossa sala de atendimento virtual: vídeo de alta definição, chat em tempo real e emissão instantânea de receitas e atestados oficiais com validação ICP-Brasil.
          </p>
        </div>

        {/* Interactive Simulator Shell */}
        <div className="max-w-5xl mx-auto rounded-3xl border-2 border-border/80 dark:border-white/10 bg-slate-950 shadow-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          {/* Left Column: Video Room View (7 cols) */}
          <div className="lg:col-span-7 flex flex-col justify-between p-4 sm:p-6 bg-slate-950 border-b lg:border-b-0 lg:border-r border-slate-800 relative min-h-[380px] sm:min-h-[460px]">
            {/* Top Bar inside Video */}
            <div className="flex items-center justify-between z-10">
              <div className="flex items-center gap-2 bg-slate-900/90 backdrop-blur-md px-3 py-1.5 rounded-xl border border-slate-700/60 text-xs text-white">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                <span className="font-bold">Dra. Camila Ramos</span>
                <span className="text-slate-400">· Cardiologia</span>
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 ml-1" />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[11px] font-mono font-bold bg-slate-900/80 text-emerald-400 border border-slate-700 px-2.5 py-1 rounded-lg">
                  HD 1080p
                </span>
              </div>
            </div>

            {/* Doctor Simulated Avatar & Video Stream */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
              {/* Animated aura rings */}
              <div className="w-64 h-64 rounded-full bg-gradient-to-tr from-blue-600/20 via-cyan-500/10 to-indigo-600/20 blur-2xl animate-pulse" />
              <div className="relative text-center">
                <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full p-1 bg-gradient-to-tr from-cyan-400 via-blue-500 to-indigo-600 mx-auto shadow-2xl relative">
                  <img
                    src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop&crop=face"
                    alt="Médica"
                    className="w-full h-full rounded-full object-cover"
                  />
                  <span className="absolute bottom-1 right-2 w-5 h-5 rounded-full bg-emerald-500 border-2 border-slate-950 shadow-md animate-pulse" />
                </div>
                <div className="mt-3">
                  <p className="text-white font-bold text-sm">Dra. Camila Ramos</p>
                  <p className="text-slate-400 text-xs">CRM 98765-SP · Conectada via Daily.co WebRTC</p>
                </div>
              </div>
            </div>

            {/* Floating Patient Self-View Window */}
            <div className="relative z-10 self-end w-28 h-20 sm:w-36 sm:h-24 rounded-2xl bg-slate-900/90 border border-slate-700 overflow-hidden shadow-xl flex items-center justify-center text-center">
              {isCameraOn ? (
                <div className="flex flex-col items-center justify-center p-2 text-slate-300">
                  <User className="w-6 h-6 text-primary mb-0.5" />
                  <span className="text-[10px] font-bold text-white">Sua Câmera (Ativa)</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-500">
                  <VideoOff className="w-5 h-5 text-rose-400 mb-0.5" />
                  <span className="text-[10px] text-rose-300">Câmera Oculta</span>
                </div>
              )}
            </div>

            {/* Bottom Call Controls Bar */}
            <div className="flex items-center justify-center gap-3 pt-4 z-10">
              {/* Mic Toggle */}
              <button
                type="button"
                onClick={() => setIsMicOn(!isMicOn)}
                className={cn(
                  'w-11 h-11 rounded-2xl flex items-center justify-center transition-all cursor-pointer shadow-lg',
                  isMicOn
                    ? 'bg-slate-800 text-white hover:bg-slate-700'
                    : 'bg-rose-500 text-white hover:bg-rose-600'
                )}
                title={isMicOn ? 'Silenciar microfone' : 'Ativar microfone'}
              >
                {isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>

              {/* Camera Toggle */}
              <button
                type="button"
                onClick={() => setIsCameraOn(!isCameraOn)}
                className={cn(
                  'w-11 h-11 rounded-2xl flex items-center justify-center transition-all cursor-pointer shadow-lg',
                  isCameraOn
                    ? 'bg-slate-800 text-white hover:bg-slate-700'
                    : 'bg-rose-500 text-white hover:bg-rose-600'
                )}
                title={isCameraOn ? 'Desligar câmera' : 'Ligar câmera'}
              >
                {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>

              {/* End Call Button */}
              <button
                type="button"
                className="px-5 h-11 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs flex items-center gap-2 transition-all cursor-pointer shadow-lg shadow-rose-600/30 active:scale-95"
                title="Simular encerramento"
                onClick={() => alert('Simulador da consulta: Para realizar uma consulta real, clique em "Agendar Consulta".')}
              >
                <PhoneOff className="w-4 h-4" />
                <span>Encerrar</span>
              </button>
            </div>
          </div>

          {/* Right Column: Interactive Chat & Document Preview (5 cols) */}
          <div className="lg:col-span-5 flex flex-col justify-between bg-slate-900 text-white p-4 sm:p-5">
            {/* Top Navigation Tabs */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-950 rounded-2xl border border-slate-800 mb-3 shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab('video')}
                className={cn(
                  'flex-1 py-1.5 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer',
                  activeTab === 'video'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                )}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>Chat</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('prescription')}
                className={cn(
                  'flex-1 py-1.5 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer',
                  activeTab === 'prescription'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                )}
              >
                <FileText className="w-3.5 h-3.5 text-cyan-400" />
                <span>Receita MeMed</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('certificate')}
                className={cn(
                  'flex-1 py-1.5 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer',
                  activeTab === 'certificate'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                )}
              >
                <FileCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>Atestado</span>
              </button>
            </div>

            {/* TAB CONTENT 1: CHAT */}
            {activeTab === 'video' && (
              <div className="flex-1 flex flex-col justify-between overflow-hidden">
                {/* Chat Messages Log */}
                <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 max-h-[300px]">
                  {messages.map((m) => {
                    const isDoctor = m.sender === 'doctor';
                    return (
                      <div
                        key={m.id}
                        className={cn('flex flex-col', isDoctor ? 'items-start' : 'items-end')}
                      >
                        <div
                          className={cn(
                            'max-w-[85%] rounded-2xl p-3 text-xs leading-relaxed',
                            isDoctor
                              ? 'bg-slate-800 text-slate-100 rounded-tl-xs border border-slate-700/60'
                              : 'bg-primary text-white rounded-tr-xs shadow-md'
                          )}
                        >
                          <p>{m.text}</p>
                          <span
                            className={cn(
                              'text-[10px] block mt-1',
                              isDoctor ? 'text-slate-400' : 'text-blue-100'
                            )}
                          >
                            {m.time}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {isTyping && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-slate-800/60 px-3 py-2 rounded-2xl w-fit">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" />
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce [animation-delay:0.2s]" />
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce [animation-delay:0.4s]" />
                      <span className="ml-1 text-[11px]">Dra. Camila digitando...</span>
                    </div>
                  )}
                </div>

                {/* Quick Prompts */}
                <div className="py-2 flex flex-wrap gap-1.5 border-t border-slate-800 mt-2">
                  {quickPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => handleSendMessage(prompt)}
                      className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-[11px] font-semibold transition-colors cursor-pointer border border-slate-700"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>

                {/* Chat Input Field */}
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Digite sua dúvida ou sintoma..."
                    className="flex-1 bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => handleSendMessage()}
                    className="p-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white transition-colors cursor-pointer"
                    aria-label="Enviar mensagem"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* TAB CONTENT 2: RECEITA DIGITAL MEMED */}
            {activeTab === 'prescription' && (
              <div className="flex-1 p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-3 animate-in fade-in duration-200">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-cyan-500/15 text-cyan-400">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Receita Médica Oficial</p>
                      <p className="text-[10px] text-slate-400">Emissor MeMed Sinapse</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    ICP-Brasil
                  </span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                    <p className="font-bold text-cyan-300">1. Dipirona Monoidratada 500mg/mL</p>
                    <p className="text-slate-400 text-[11px] mt-0.5">Tomar 35 gotas de 6 em 6 horas se dor ou febre.</p>
                  </div>
                  <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
                    <p className="font-bold text-cyan-300">2. Loratadina 10mg</p>
                    <p className="text-slate-400 text-[11px] mt-0.5">Tomar 1 comprimido à noite por 5 dias.</p>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <QrCode className="w-8 h-8 text-white" />
                    <div>
                      <p className="text-[11px] font-bold text-white">QR Code de Dispensação</p>
                      <p className="text-[10px] text-slate-400">Válido em qualquer farmácia</p>
                    </div>
                  </div>
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
              </div>
            )}

            {/* TAB CONTENT 3: ATESTADO MÉDICO */}
            {activeTab === 'certificate' && (
              <div className="flex-1 p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-3 animate-in fade-in duration-200">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-400">
                      <FileCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Atestado de Afastamento</p>
                      <p className="text-[10px] text-slate-400">Resolução CFM 2.299/2021</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    Assinado
                  </span>
                </div>

                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-xs space-y-1.5">
                  <p className="text-slate-300 leading-relaxed">
                    Atesto para os devidos fins que o paciente necessita de <strong>2 (dois) dias de repouso</strong> por motivo de doença.
                  </p>
                  <p className="text-[11px] text-slate-400">
                    Diagnóstico Clínico: <strong className="text-white">CID-10 J06.9 (Infecção respiratória aguda)</strong>
                  </p>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between text-[11px]">
                  <span className="text-slate-400">Hash de Validação:</span>
                  <span className="font-mono text-cyan-300 font-bold">a8f9c2...d71e</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Unified Patient Area Shortcut Banner */}
        <div className="mt-12 max-w-4xl mx-auto rounded-3xl border border-border/80 dark:border-white/10 bg-card p-6 sm:p-8 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <div>
              <h3 className="font-extrabold text-lg text-foreground">
                Área do Paciente Integrada
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Acesse suas receitas com QR Code, atestados médicos assinados e seu prontuário digital unificado em um só lugar.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
            <Link to="/paciente/consultas" className="w-full sm:w-auto">
              <Button
                variant="outline"
                className="w-full sm:w-auto rounded-xl font-bold text-xs h-11 border-border hover:border-primary"
              >
                Minhas Consultas
              </Button>
            </Link>
            <Link to="/cadastro" className="w-full sm:w-auto">
              <Button
                className="w-full sm:w-auto rounded-xl font-bold text-xs h-11 bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white shadow-md shadow-primary/20 gap-1.5"
              >
                <span>Criar Conta Gratuita</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
